import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import EntityListView from '../listView/EntityListView';
import { UsersApi, AgentBalancesApi } from '../../../api/common/apiIndex';
import showConfirmToast from '../businessUtilities/ConfirmToast';
import { toast } from 'react-toastify';
import useDebounce from '../hooks/useDebounce';
import EditEntityModal from '../../common/commonUtils/EditEntityModal';
import Filters from '../businessUtilities/Filters';
import { useIsAdmin } from '../hooks/useIsAdmin';
import { useIsDarkTheme } from '../hooks/useIsDarkTheme';
import Loader from '../../common/commonUtils/Loader';

const fmtMoney = (n, currency = 'SEK') =>
  new Intl.NumberFormat('sv-SE', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(n || 0));

const Agents = () => {
  const [page, setPage] = useState(0);
  const size = 10;
  const sort = 'firstName,asc';

  const [searchTerm, setSearchTerm] = useState('');
  const [country, setCountry] = useState('');
  const q = useDebounce(searchTerm, 350);
  const [editingRow, setEditingRow] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const isDark = useIsDarkTheme();

  useEffect(() => {
    setPage(0);
  }, [q, country]);

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isAdmin = useIsAdmin();

  // Agents list (paged)
  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
  } = useQuery({
    queryKey: ['agents', { page, size, sort, q, country }],
    queryFn: () => UsersApi.listAgents({ page, size, sort, q, country }),
    keepPreviousData: true,
    staleTime: 1000 * 60 * 10,
    placeholderData: (prev) => prev,
  });

  const {
    data: balancesPage,
    isFetching: isFetchingBalances,
  } = useQuery({
    queryKey: ['agent-balance-snapshots', { q, country }],
    queryFn: () =>
      AgentBalancesApi.list({
        page: 0,
        size: 500, 
        sort: 'agentId,asc',
        q,
        country,
      }),
    keepPreviousData: true,
    staleTime: 1000 * 60 * 10,
    placeholderData: (prev) => prev,
  });

  const agents = data?.content ?? [];
  const totalPages = data?.totalPages ?? 0;
  const totalElements = data?.totalElements ?? 0;

  const snapByAgentId = useMemo(() => {
    const m = new Map();
    const list = balancesPage?.content ?? [];
    list.forEach((s) => {
      if (s?.agentId != null) m.set(s.agentId, s);
    });
    return m;
  }, [balancesPage]);

  // --- Stats ---
  const fundedAgents = useMemo(() => {
    const s = balancesPage?.content ?? [];
    const set = new Set();
    s.forEach((row) => {
      const avail = Number(row?.available ?? 0);
      if (row?.agentId != null && avail > 0) set.add(row.agentId);
    });
    return set.size;
  }, [balancesPage]);

  const deleteMutation = useMutation({
    mutationFn: (id) => UsersApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agents'] });
      toast.success('Agent deleted.');
    },
    onError: (err) => {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data ||
        err?.message ||
        'Failed to delete agent.';
      toast.error(msg);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, dto }) => UsersApi.update(id, dto),
    onSuccess: () => {
      setEditingRow(null);
      queryClient.invalidateQueries({ queryKey: ['agents'] });
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['agent-balance-snapshots'] });
      toast.success('Agent updated successfully');
    },
    onError: (err) => {
      const message =
        err?.response?.data?.message ||
        err?.response?.data ||
        err?.message ||
        'An unexpected error occurred';
      toast.error(message);
    },
  });

  const handleDelete = (id) => {
    showConfirmToast({
      message: 'Are you sure you want to delete this agent?',
      onConfirm: () => deleteMutation.mutate(id),
      onCancel: () => { },
      id: `delete-agent-${id}`,
    });
  };

  const handleEdit = (id) => {
    const agent = agents.find((a) => a.id === id);
    if (!agent) return;
    setEditingRow(agent);
  };

  const handleUpdateSubmit = (dto) => {
    if (!editingRow?.id) {
      toast.error('Missing entity ID.');
      return;
    }
    updateMutation.mutate({ id: editingRow.id, dto });
  };

  const clearFilters = () => {
    setCountry('');
    setSearchTerm('');
  };

  const rangeText = useMemo(() => {
    if (!totalElements) return '0–0 of 0';
    const start = page * size + 1;
    const end = Math.min(start + agents.length - 1, totalElements);
    return `${start}–${end} of ${totalElements}`;
  }, [page, size, agents.length, totalElements]);

  if (isLoading && !data) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <Loader text="Loading…" />
      </div>
    );
  }

  const renderItem = ({ item }) => {
    const snap = snapByAgentId.get(item.id);
    const currency = snap?.currency || 'SEK';
    const amount = fmtMoney(snap?.available ?? 0, currency);

    return (
      <div className="d-flex align-items-center gap-2">
        <i className="bi bi-wallet2" aria-hidden="true" />
        <div className="d-flex flex-column lh-1">
          <small className="text-body-secondary">Available</small>
          <strong className="fs-6 text-nowrap">
            {amount}
          </strong>
        </div>
        {isFetchingBalances && (
          <Loader text={"updating…"} size='sm'/>
        )}
      </div>
    );
  };

  const goToCreateAgent = () =>
    navigate('/home/create-user', { state: { role: 'AGENT', showBackButton: true } });

  return (
    <div className="w-100 d-flex flex-column gap-3">
      <div className="card border shadow rounded-4">
        <div className="card-body">
          <div className="row g-3 align-items-stretch">
            <div className="col-12 col-md-6">
              <div className="h-100 p-3 rounded-4 border bg-body-tertiary">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <div className="d-flex align-items-center gap-2">
                    <i className="bi bi-people fs-5" aria-hidden />
                    <h6 className="mb-0">Agents</h6>
                  </div>
                  <span className="badge rounded-pill bg-body border text-body">
                    <strong>{totalElements}</strong>
                  </span>
                </div>
                <p className="text-body-secondary small mb-0">Total number of agents.</p>
              </div>
            </div>

            <div className="col-12 col-md-6">
              <div className="h-100 p-3 p-md-4 rounded-4 border bg-body-tertiary">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <div className="d-flex align-items-center gap-2">
                    <i className="bi bi-wallet2 fs-5" aria-hidden />
                    <h6 className="mb-0">Funded agents</h6>
                  </div>
                  <span className="badge rounded-pill bg-body border text-body">
                    <strong>{fundedAgents}</strong>
                  </span>
                </div>
                <p className="text-body-secondary small mb-0">
                  Agents with a balance &gt; 0 across all countries.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card border shadow rounded-4 sticky-top">
        <div className="card-body">
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <button
                className={`btn btn-outline-secondary btn-sm rounded-pill d-inline-flex align-items-center gap-2 ${isDark ? 'text-white' : ''}`}
                onClick={() => setShowFilters((s) => !s)}
                aria-expanded={showFilters}
                aria-controls="agent-filters"
                disabled={isFetching}
              >
                <i className="bi bi-funnel" />
                {showFilters ? 'Hide filters' : 'Show filters'}
              </button>
            </div>

            <div className="d-flex align-items-center gap-2">
              {isAdmin && (
                <button
                  className={`btn btn-outline-secondary btn-sm rounded-pill ${isDark ? 'text-white': ''}`}
                  onClick={goToCreateAgent}
                  title="Add agent"
                  type="button"
                >
                  <i className="bi bi-person-plus me-1" aria-hidden="true" /> Add Agent
                </button>
              )}
            </div>
          </div>

          {(q || country) && (
            <div className="mt-2 d-flex align-items-center gap-2 flex-wrap">
              <small className="text-body-secondary me-1">Filters:</small>
              {country && (
                <span className="badge bg-body-tertiary border text-body">Country: {country}</span>
              )}
              {q && <span className="badge bg-body-tertiary border text-body">Search: “{q}”</span>}
              <button className="btn btn-sm btn-link text-decoration-none" onClick={clearFilters}>
                Clear filters
              </button>
            </div>
          )}

          {showFilters && (
            <div className="mt-3" id="agent-filters">
              <Filters
                data={agents}
                getCountry={(a) => a.country}
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                selectedCountry={country}
                onCountryChange={setCountry}
              />
            </div>
          )}
        </div>
      </div>

      <div className="card border shadow rounded-4">
        <div className="card-body">
          {isError && (
            <div className="alert alert-danger" role="alert">
              Failed to load agents. {error?.message || ''}
            </div>
          )}

          {!isError && (
            <>
              <EntityListView
                base="agent"
                data={agents}
                getCountry={(a) => a.country}
                onEdit={handleEdit}
                onDelete={handleDelete}
                emptyMessage="No agents found."
                totalCount={totalElements}
                isLoading={isFetching && !!data}
                renderItem={renderItem}
              />

              <div className="d-flex w-100 justify-content-between align-items-center mt-3">
                <small className="text-body-secondary">{rangeText}</small>
                <div className="d-flex align-items-center gap-2">
                  <button
                    className="btn btn-outline-secondary btn-sm rounded-pill"
                    disabled={page === 0 || isFetching}
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                  >
                    Prev
                  </button>
                  <span className="text-body-secondary small">
                    Page {totalPages === 0 ? 0 : page + 1} / {totalPages}
                    {isFetching ? ' …' : ''}
                  </span>
                  <button
                    className="btn btn-outline-secondary btn-sm rounded-pill"
                    disabled={page + 1 >= totalPages || isFetching}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {editingRow && (
        <EditEntityModal
          title={'Agent'}
          row={editingRow}
          isBusy={updateMutation.isPending}
          onClose={() => setEditingRow(null)}
          onSubmit={handleUpdateSubmit}
        />
      )}
    </div>
  );
};

export default Agents;
